import { confirmFmlmSearchAction } from "../../../../Actions/UIActions/FmlmSearch/ConfirmFmlmSearchAction";
import { searchLocationAction } from "../../../../Actions/UIActions/FmlmSearch/FmlmSearchLocation";
import { selectLocationAction } from "../../../../Actions/UIActions/FmlmSearch/FmlmSelectLocationAction";
import { openFmlmSubStationDropdownAction } from "../../../../Actions/UIActions/FmlmSearch/OpenFmlmSubStationDropdownAction";
import { selectedStopsForOriginAndDestination } from "../../../../Actions/UIActions/FmlmSearch/SelectedStopsForOriginAndDestination";
import { updatedFmlmStopAction } from "../../../../Actions/UIActions/FmlmSearch/UpdatedFmlmStopsAction";
import { opencloseLocationMap } from "../../../../Actions/UIActions/Search/OpenCloseLocationMap";
import { ExecutionStatus } from "../../../../core/enum/ExecutionStatus";
import { _eventBus } from "../../../../core/events/EventBus";
import { StepDefinition } from "../../../../core/flows/StepDefinition";
import { StepFlow } from "../../../../core/flows/StepFlow";
import { fmlmSearchTree } from "../../../../Orchestrator/ConstantTree/FmlmSearch/FmlmSearchTree";
import { SearchStateType } from "../../../../Orchestrator/Types/Index";
import {
  LocationPermission,
  LocationState,
  SelectedStop,
  SelectLocationForFmlm,
} from "../../../../Orchestrator/Types/Types/FmlmSearch/FmlmSearchType";
import { SearchContext } from "../Context/SearchContext";
import { fmlmReverseGeocodeAction } from "../../../../Actions/UIActions/FmlmSearch/ReverseGeocodeAction";
import { fmlmCurrentLocationAction } from "../../../../Actions/UIActions/FmlmSearch/FmlmCurrentLocationAction";
import { favoriteStopAction } from "../../../../Actions/UIActions/Search/FavoriteStopAction";
import { favouriteFmlmLocationAction } from "../../../../Actions/UIActions/Search/FavouriteFmlmLocationAction";
import { IFavoriteStopActionParams } from "../../../../Orchestrator/Types/Interface/ActionParams/FavoriteStopActionParams";
import { IConfirmFmlmSearchActionParams } from "../../../../Orchestrator/Types/Interface/ActionParams/ConfirmFmlmSearchActionParams";
import { IFavoriteFmlmLocationActionParams } from "../../../../Orchestrator/Types/Interface/ActionParams/FavouriteFmlmLocationActionParams";
import { storeLastFmlmSearchAction } from "../../../../Actions/UIActions/FmlmSearch/StoreLastFmlmSearchAction";
import { journeyLocationSelectionAction } from "../../../../Actions/UIActions/BookingJourneyPlanner/JourneyLocationSelectionAction";
import { getRecoilStateValue } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { LocationEnum, TripTypeEnum } from "../../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IJourneyLocationSelectionActionParams } from "../../../../Orchestrator/Types/Interface/ActionParams/JourneyLocationSelectionActionParams";

class FmlmSearchStep extends StepFlow {
  get stepName() {
    return "FmlmSearchStep";
  }

  async onEventSetup(): Promise<void> {
    this.registerEventSubscription(
      _eventBus.on<{ searchValue: string; locationPermission: LocationPermission }, void>({
        eventName: fmlmSearchTree.Search.SearchOnChange,
        handler: async (payload) => {
          let context = this.getCtx<SearchContext>("searchContext")!;
          context.locationPermission = payload.data?.locationPermission!;
          await searchLocationAction.setParams({
            q: payload.data?.searchValue!,
            locationPermission: context.locationPermission,
          });
          await searchLocationAction.execute();
          if (searchLocationAction.status.executionStatus === ExecutionStatus.SUCCESS) {
            context.locationData = searchLocationAction.result?.features ?? [];
          }
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on<SelectLocationForFmlm, void>({
        eventName: fmlmSearchTree.selectLocationForOrigin.select,
        handler: async ({ data }) => {
          const context = this.getCtx<SearchContext>("searchContext")!;
          context.selectedLocationForOrigin = data?.location!;
          context.searchStateAtom = data?.search!;
          if (data?.location) {
            let distanceFromStart = context.selectedLocationForOrigin.properties?.distanceFromStart;
            selectLocationAction.setParams({
              ...data,
              name: context.selectedLocationForOrigin.properties.name,
              place_formatted: context.selectedLocationForOrigin.properties.place_formatted,
              coordinates: context.selectedLocationForOrigin.geometry.coordinates,
              ...(distanceFromStart && { distanceFromStart }),
            });
            await selectLocationAction.execute();
            if (selectLocationAction.status.executionStatus === ExecutionStatus.SUCCESS) {
              context.searchStateAtom = selectLocationAction.result!;
            }
          }
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on<SelectLocationForFmlm, void>({
        eventName: fmlmSearchTree.selectLocationForDestination.select,
        handler: async ({ data }) => {
          const context = this.getCtx<SearchContext>("searchContext")!;
          context.selectedLocationForDestination = data?.location!;
          context.searchStateAtom = data?.search!;
          if (data?.location) {
            await selectLocationAction.setParams({
              ...data,
              name: data?.location.properties.name,
              place_formatted: data?.location.properties.place_formatted,
              coordinates: data?.location.geometry.coordinates,
              distanceFromStart: data?.location.properties?.distanceFromStart,
            });
            await selectLocationAction.execute();
            if (selectLocationAction.status.executionStatus === ExecutionStatus.SUCCESS) {
              context.searchStateAtom = selectLocationAction.result!;
            }
          }
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on<{ dropdownName: string; openDropdowns: any }, void>({
        eventName: fmlmSearchTree.SubStations.Click,
        handler: (payload) => {
          openFmlmSubStationDropdownAction.setParams(payload.data!);
          openFmlmSubStationDropdownAction.execute();
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on<IFavoriteStopActionParams, void>({
        eventName: fmlmSearchTree.Favourite.Stop.Click,
        handler: (payload) => {
          favoriteStopAction.setParams(payload.data!)
          favoriteStopAction.execute()
        },
      })
    );
    this.registerEventSubscription(
      _eventBus.on<IFavoriteFmlmLocationActionParams, void>({
        eventName: fmlmSearchTree.Favourite.Location.Click,
        handler: async (payload) => {
          favouriteFmlmLocationAction.setParams({
            userData: payload.data?.userData!,
            locationState: payload.data?.locationState!,
            selectedExactLocation: payload.data?.selectedExactLocation!,
            intl: payload.data?.intl!,
          });
          await favouriteFmlmLocationAction.execute();
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on<{ data: any; locationPermission: LocationPermission }, void>({
        eventName: fmlmSearchTree.Stops.Updated,
        handler: async (payload) => {
          await updatedFmlmStopAction.setParams(payload.data!);
          await updatedFmlmStopAction.execute();
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on<IJourneyLocationSelectionActionParams, void>({
        eventName: fmlmSearchTree.JourneyPlannerSelectLocation.Click,
        handler: async (payload) => {
          const journeyPlannerTrainTypeTabAtom = getRecoilStateValue<TripTypeEnum>("journeyPlannerTrainTypeTabAtom")

          journeyLocationSelectionAction.setParams({
            locationData: payload.data?.locationData! as LocationState,
            locationType: payload.data?.locationType as LocationEnum,
            trainType: journeyPlannerTrainTypeTabAtom,
            visible: payload.data?.visible!,
          });
          await journeyLocationSelectionAction.execute();

          let context = this.getCtx<SearchContext>("searchContext")!;
          context.routeTo = ["nextStep"];
          this.next();
        }
      })
    );

    this.registerEventSubscription(
      _eventBus.on<IConfirmFmlmSearchActionParams, void>({
        eventName: fmlmSearchTree.ConfirmFmlmSearch.Click,
        handler: async (payload) => {
          let context = this.getCtx<SearchContext>("searchContext")!;
          context.locationMapVisible = payload.data?.visible!;
          confirmFmlmSearchAction.setParams({
            search: payload.data?.search!,
            locationData: payload.data?.locationData!,
            visible: payload.data?.visible!,
            selectedExactLocation: payload.data?.selectedExactLocation!,
            isFromPopUp: payload.data?.isFromPopUp || false,
          });
          await confirmFmlmSearchAction.execute();

          if (payload.data?.userData) {
            // storing fmlm last search location to users preferences
            storeLastFmlmSearchAction.setParams({
              userData: payload.data?.userData!,
              locationData: payload.data?.locationData!,
              selectedExactLocation: payload.data?.selectedExactLocation!,
            });
            await storeLastFmlmSearchAction.execute();
          }

          let nextStep = payload.data?.selectedExactLocation === "destination" ? "SearchStartupInit_Step" : "SelectSearchStop_Step";
          context.routeTo = [nextStep];
          this.next();
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on<{ visible: boolean; locationFor: string; search: SearchStateType }, void>({
        eventName: fmlmSearchTree.close.Click,
        handler: async (payload) => {
          const context = this.getCtx<SearchContext>("searchContext")!;
          context.locationMapVisible = payload.data?.visible!;
          await opencloseLocationMap.setParams({
            isVisible: context.locationMapVisible,
            locationFor: payload.data?.locationFor!,
            search: payload.data?.search!,
          });
          await opencloseLocationMap.execute();
          context.routeTo = ["SelectSearchStop_Step"];
          this.next();
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on<SelectedStop, void>({
        eventName: fmlmSearchTree.selectStops.Select,
        handler: async (payload) => {
          let context = this.getCtx<SearchContext>("searchContext")!;
          context.selectedLocation = payload.data!;
          await selectedStopsForOriginAndDestination.setParams({ ...context.selectedLocation });
          await selectedStopsForOriginAndDestination.execute();

          if (
            selectedStopsForOriginAndDestination.status.executionStatus === ExecutionStatus.SUCCESS
          ) {
            context.searchStateAtom = selectedStopsForOriginAndDestination.result!;
          }
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on<{ longitude: number; latitude: number }, void>({
        eventName: fmlmSearchTree.ReverseGeocode.Execute,
        handler: async (payload) => {
          await fmlmReverseGeocodeAction.setParams({
            longitude: payload.data?.longitude!,
            latitude: payload.data?.latitude!,
          });
          await fmlmReverseGeocodeAction.execute();
          if (fmlmReverseGeocodeAction.status.executionStatus === ExecutionStatus.SUCCESS) {
            let context = this.getCtx<SearchContext>("searchContext")!;
            context.reverseGeocodeLocation = fmlmReverseGeocodeAction.result!;
          }
        },
      })
    );

    this.registerEventSubscription(
      _eventBus.on<{ longitude: number; latitude: number }, void>({
        eventName: fmlmSearchTree.CurrentLocation.Click,
        handler: async (payload) => {
          await fmlmCurrentLocationAction.setParams({
            longitude: payload.data?.longitude!,
            latitude: payload.data?.latitude!,
          });
          await fmlmCurrentLocationAction.execute();
          if (fmlmCurrentLocationAction.status.executionStatus === ExecutionStatus.SUCCESS) {
            let context = this.getCtx<SearchContext>("searchContext")!;
            context.currentLocation = fmlmCurrentLocationAction.result!;
          }
        },
      })
    );
  }

  async onExecute(): Promise<void> {
    console.log("Fmlm Search Init Step Executed..............");
  }
}

export const FmlmSearchStepDeclaration = new StepDefinition({
  stepName: "FmlmSearchStep",
  stepClass: FmlmSearchStep,
});
